import React, { useEffect, useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PropTypes from "prop-types";
import {  useHistory } from "react-router-dom";
import axios from "axios";
import {employernationalities as nationalities} from "../../assets/nationalities";
// Validation Schema
const employerValidationSchema = yup.object().shape({
  // Employer fields
  salutation: yup.string(),
  employer_name: yup.string().required("Employer Name is required"),
  nric_passport: yup.string().required("NRIC/Passport No. is required"),
  fin: yup.string(),
  citizenship: yup.string(),
  residence_status: yup.string(),
  date_of_birth: yup.date().required("Date of Birth is required").typeError("Date of Birth is required"),
  occupation: yup.string(),
  company: yup.string(),
  religion: yup.string(),
  marital_status: yup.string(),
  marriage_registered: yup.boolean(),
  property_type: yup.string(),
  other_property_type: yup.string(),
  no_of_levels: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  no_of_rooms: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  no_of_washrooms: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  employer_type: yup.string(),
  
  // Household members
  adults_m: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  adults_f: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  babies_m: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  babies_f: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  children_m: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  children_f: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  elderly_m: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),
  elderly_f: yup.number().transform((value, originalValue) => originalValue === "" ? 0 : value).min(0),

  // Spouse fields
  spouse_salutation: yup.string(),
  spouse_name: yup.string(),
  spouse_nric_passport: yup.string(),
  spouse_citizenship: yup.string(),
  spouse_date_of_birth: yup.date().nullable().transform((value, originalValue) => originalValue === "" ? null : value),
  spouse_occupation: yup.string(),
  spouse_company: yup.string(),
  spouse_religion: yup.string(),
  spouse_remarks: yup.string(),

  // Contact fields
  address_line1: yup.string(),
  address_line2: yup.string(),
  contact_no: yup.string(),
  home_no: yup.string(),
  office_no: yup.string(),
  email_address: yup.string().email("Invalid email format"),

  // Job scope fields
  cooking_food: yup.string(),
  baby_care: yup.boolean(),
  infant_care: yup.boolean(),
  child_care: yup.boolean(),
  elderly_care: yup.boolean(),
  elderly_nursing: yup.boolean(),
  invalid_care: yup.boolean(),
  gardening: yup.boolean(),
  laundry: yup.boolean(),
  marketing: yup.boolean(),
  pet_care: yup.boolean(),
  car_wash: yup.boolean(),
  job_remarks: yup.string()
});

// Small helpers
const toId = (prefix, label, idx) =>
  `${prefix}-${label}`.replace(/[^a-z0-9]+/gi, "-").replace(/-+/g, "-").toLowerCase() + (idx != null ? `-${idx}` : "");

const Field = ({ id, label, type = "text", required = false, placeholder, error, ...fieldProps }) => (
  <div className="mb-3">
    <label className={`form-label`} htmlFor={id}>
      {label}{required ? " *" : ""}
    </label>
    <input 
      id={id} 
      type={type} 
      className={`form-control${error ? " is-invalid" : ""}`}
      placeholder={placeholder || ""} 
      {...fieldProps}
    />
    {error && <div className="invalid-feedback">{error.message}</div>}
  </div>
);

const SelectField = ({ id, label, options = [], required = false, error, ...fieldProps }) => (
  <div className="mb-3">
    <label className={`form-label`} htmlFor={id}>
      {label}{required ? " *" : ""}:
    </label>
    <select id={id} className={`form-select${error ? " is-invalid" : ""}`} {...fieldProps}>
      <option value="">Select...</option>
      {options.length > 0 ? options.map((opt, i) => <option key={i} value={opt}>{opt}</option>) : null}
    </select>
    {error && <div className="invalid-feedback">{error.message}</div>}
  </div>
);

const TextAreaField = ({ id, label, placeholder, error, ...fieldProps }) => (
  <div className="mb-3">
    <label className="form-label" htmlFor={id}>
      {label}:
    </label>
    <textarea 
      id={id} 
      className={`form-control${error ? " is-invalid" : ""}`}
      placeholder={placeholder || ""}
      {...fieldProps}
    ></textarea>
    {error && <div className="invalid-feedback">{error.message}</div>}
  </div>
);

const CheckField = ({ id, label, error, ...fieldProps }) => (
  <div className="form-check">
    <input type="checkbox" className="form-check-input" id={id} {...fieldProps} />
    <label className="form-check-label" htmlFor={id}>
      {label}
    </label>
    {error && <div className="text-danger small mt-1">{error.message}</div>}
  </div>
);

// Main component
const FdwEmployerForm = () => {
    const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(false);

  const methods = useForm({
    resolver: yupResolver(employerValidationSchema),
    mode: "onBlur",
    defaultValues: {
      salutation: "",
      employer_name: "",
      nric_passport: "",
      fin: "",
      citizenship: "",
      residence_status: "",
      date_of_birth: "",
      occupation: "",
      company: "",
      religion: "",
      marital_status: "",
      marriage_registered: false,
      property_type: "",
      other_property_type: "",
      no_of_levels: 0,
      no_of_rooms: 0,
      no_of_washrooms: 0,
      employer_type: "",
      
      // Household members
      adults_m: 0,
      adults_f: 0,
      babies_m: 0,
      babies_f: 0,
      children_m: 0,
      children_f: 0,
      elderly_m: 0,
      elderly_f: 0,

      // Spouse
      spouse_salutation: "",
      spouse_name: "",
      spouse_nric_passport: "",
      spouse_citizenship: "",
      spouse_date_of_birth: "",
      spouse_occupation: "",
      spouse_company: "",
      spouse_religion: "",
      spouse_remarks: "",

      // Contact
      address_line1: "",
      address_line2: "",
      contact_no: "",
      home_no: "",
      office_no: "",
      email_address: "",

      // Job scope
      cooking_food: "",
      baby_care: false,
      infant_care: false,
      child_care: false,
      elderly_care: false,
      elderly_nursing: false,
      invalid_care: false,
      gardening: false,
      laundry: false,
      marketing: false,
      pet_care: false,
      car_wash: false,
      job_remarks: ""
    }
  });

  const { handleSubmit, control, register, trigger, setError, formState: { errors } } = methods;

  const url = window.location.href;

  const idMatch = url.match(/a=([^&]+)/);
  const tokenMatch = url.match(/token=([^&]+)/);

  const idagency = idMatch ? idMatch[1] : null;
  const token = tokenMatch ? tokenMatch[1] : null;
  useEffect(() => {
     
  if (!idagency || !token) {
     history.push("/login");
  }
  }, []);
  useEffect(() => {
    console.log("Current errors:", errors);
  }, [errors]);

  const onSubmit = async (data, e) => {
    // Validate all fields before submission
    const isValid = await trigger();
    
    if (!isValid) {
      setError("root", { 
        type: "validation", 
        message: "Please correct the following errors before submitting the form." 
      });
      return;
    }
    
    setLoading(true);
    try {
      // Transform empty strings to 0 for number fields
      const transformedData = {
        ...data,
        no_of_levels: data.no_of_levels === "" ? 0 : Number(data.no_of_levels) || 0,
        no_of_rooms: data.no_of_rooms === "" ? 0 : Number(data.no_of_rooms) || 0,
        no_of_washrooms: data.no_of_washrooms === "" ? 0 : Number(data.no_of_washrooms) || 0,
        adults_m: data.adults_m === "" ? 0 : Number(data.adults_m) || 0,
        adults_f: data.adults_f === "" ? 0 : Number(data.adults_f) || 0,
        babies_m: data.babies_m === "" ? 0 : Number(data.babies_m) || 0,
        babies_f: data.babies_f === "" ? 0 : Number(data.babies_f) || 0,
        children_m: data.children_m === "" ? 0 : Number(data.children_m) || 0,
        children_f: data.children_f === "" ? 0 : Number(data.children_f) || 0,
        elderly_m: data.elderly_m === "" ? 0 : Number(data.elderly_m) || 0,
        elderly_f: data.elderly_f === "" ? 0 : Number(data.elderly_f) || 0,
      };
      
      console.log("Form data:", transformedData);
      const dataAll = { data: transformedData, agency_id: idagency, token: token };
      // Here you would typically send the data to your API
       await axios.post(process.env.REACT_APP_BASE_URL+"/collect-info-employer/", dataAll);

      setSubmitStatus(true);
    } catch (error) {
     let errorMessage = "";
            const responseCode = error.response ? error.response.status : "Unknown";
            console.error("API Error - Response Code:", responseCode);
            
            if (error?.response?.data) {
                Object.entries(error.response.data).forEach(([key, values]) => {
                    errorMessage += `${key} : ${values}\n`;
                });
            }
            
            if (!errorMessage) {
                errorMessage = "Failed to generate collection link. Please try again.";
            }
      setError("root", { 
        type: "server", 
        message: errorMessage 
      });
    } finally {
      setLoading(false);
    }
  };

  // Column 1 - Employer
  const employerTop = [
    { as: "select", name: "salutation", label: "Salutation", options: ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."] },
    { as: "input", name: "employer_name", label: "Employer Name", required: true },
    { as: "input", name: "nric_passport", label: "NRIC/Passport No.", required: true },
    { as: "input", name: "fin", label: "FIN" },
    { as: "select", name: "citizenship", label: "Citizenship", options: nationalities },
    { as: "select", name: "residence_status", label: "Residence Status", options: ["Citizen", "Permanent Resident"] },
    { as: "input", name: "date_of_birth", label: "Date of Birth", type: "date" ,required:true },
    { as: "input", name: "occupation", label: "Occupation" },
    { as: "input", name: "company", label: "Company" },
    { as: "select", name: "religion", label: "Religion",options: ["Christian", "Catholic", "Muslim", "Hindu", "Buddhist", "Sikh", "Other"] },
    { as: "select", name: "marital_status", label: "Marital Status",options:["Single", "Married", "Divorced", "Widowed"] }
  ];

  const employerProperty = [
    { as: "select", name: "property_type", label: "Property Type",options:["Single-family homes", "Apartments", "Condominiums", "Townhouses", "Duplexes"] },
    { as: "input", name: "other_property_type", label: "Other Property Type" }
  ];

  // Column 2 - Spouse + Contact
  const spouseFields = [
    { as: "select", name: "spouse_salutation", label: "Salutation", options:  ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."]  },
    { as: "input", name: "spouse_name", label: "Spouse Name" },
    { as: "input", name: "spouse_nric_passport", label: "NRIC/Passport No." },
    { as: "select", name: "spouse_citizenship", label: "Citizenship",options: nationalities },
    { as: "input", name: "spouse_date_of_birth", label: "Date of Birth", type: "date" },
    { as: "input", name: "spouse_occupation", label: "Occupation" },
    { as: "input", name: "spouse_company", label: "Company" },
    { as: "select", name: "spouse_religion", label: "Religion" ,options: ["Christian", "Catholic", "Muslim", "Hindu", "Buddhist", "Sikh", "Other"]},
  ];

  const contactFields = [
    { as: "input", name: "address_line1", label: "Address Line 1"  },
    { as: "input", name: "address_line2", label: "Address Line 2" },
    { as: "input", name: "contact_no", label: "Contact No." },
    { as: "input", name: "home_no", label: "Home No." },
    { as: "input", name: "office_no", label: "Office No." },
    { as: "input", name: "email_address", label: "Email Address", type: "email" }
  ];

  // Column 3 - Job Scope
  const jobScopeChecks = [
    { name: "baby_care", label: "Baby Care" },
    { name: "infant_care", label: "Infant Care" },
    { name: "child_care", label: "Child Care" },
    { name: "elderly_care", label: "Elderly Care" },
    { name: "elderly_nursing", label: "Elderly Care (Nursing)" },
    { name: "invalid_care", label: "Invalid Care" },
    { name: "gardening", label: "Gardening" },
    { name: "laundry", label: "Laundry" },
    { name: "marketing", label: "Marketing" },
    { name: "pet_care", label: "Pet Care" },
    { name: "car_wash", label: "Car Wash" }
  ];

  if (submitStatus) {
    return (
      <div className="container p-4">
        <div className="alert alert-success">
          <h4>Form Submitted Successfully!</h4>
          <p>The employer form has been submitted successfully.</p>
         
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit((data, e) => onSubmit(data, e))}>
        <div className="container p-4">
          <div className="row g-4">
            {/* FDW's Employer */}
            <div className="col-md-4">
              <h5 className="text-primary">FDW&apos;s Employer</h5>

              {employerTop.map((f, i) =>
                f.as === "select" ? (
                  <Controller
                    key={i}
                    name={f.name}
                    control={control}
                    render={({ field, fieldState }) => (
                      <SelectField
                        id={toId("employer", f.label, i)}
                        label={f.label}
                        options={f.options}
                        required={f.required}
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    key={i}
                    name={f.name}
                    control={control}
                    render={({ field, fieldState }) => (
                      <Field
                        id={toId("employer", f.label, i)}
                        label={f.label}
                        type={f.type}
                        required={f.required}
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                )
              )}

              <Controller
                name="marriage_registered"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="form-check mb-3">
                    <input 
                      type="checkbox" 
                      className="form-check-input" 
                      id="marriage_registered"
                      {...field}
                      checked={field.value}
                    />
                    <label className="form-check-label" htmlFor="marriage_registered">
                      Marriage Registered in Singapore?
                    </label>
                    {fieldState.error && <div className="text-danger small mt-1">{fieldState.error.message}</div>}
                  </div>
                )}
              />

              {employerProperty.map((f, i) =>
                f.as === "select" ? (
                  <Controller
                    key={i}
                    name={f.name}
                    control={control}
                    render={({ field, fieldState }) => (
                      <SelectField
                        id={toId("employer-prop", f.label, i)}
                        label={f.label}
                         options={f.options}
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    key={i}
                    name={f.name}
                    control={control}
                    render={({ field, fieldState }) => (
                      <Field
                        id={toId("employer-prop", f.label, i)}
                        label={f.label}
                       
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                )
              )}

              <div className="row mb-3">
                <div className="col">
                  <Controller
                    name="no_of_levels"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Field
                        id="employer-levels"
                        label="No. of Level"
                        type="number"
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                </div>
                <div className="col">
                  <Controller
                    name="no_of_rooms"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Field
                        id="employer-rooms"
                        label="No. of Room"
                        type="number"
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                </div>
                <div className="col">
                  <Controller
                    name="no_of_washrooms"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Field
                        id="employer-washrooms"
                        label="No.Washrooms"
                        type="number"
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>

              <Controller
                name="employer_type"
                control={control}
                render={({ field, fieldState }) => (
                  <SelectField
                    id="employer-type"
                    label="Employer Type"
                    error={fieldState.error}
                    options={["Chinese", "Indian", "Expat", "Malay", "Other"]}
                    {...field}
                  />
                )}
              />

              <div className="mb-3">
                <label className="form-label">No. of Household Members:</label>
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Male</th>
                      <th>Female</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: "Adults", maleField: "adults_m", femaleField: "adults_f" },
                      { label: "New Born Baby", maleField: "babies_m", femaleField: "babies_f" },
                      { label: "Children", maleField: "children_m", femaleField: "children_f" },
                      { label: "Elderly", maleField: "elderly_m", femaleField: "elderly_f" }
                    ].map((item, i) => (
                      <tr key={item.label}>
                        <td>{item.label}</td>
                        <td>
                          <input 
                            type="number" 
                            className="form-control" 
                            id={toId("hh", `${item.label}-m`, i)}
                            {...register(item.maleField)}
                          />
                        </td>
                        <td>
                          <input 
                            type="number" 
                            className="form-control" 
                            id={toId("hh", `${item.label}-f`, i)}
                            {...register(item.femaleField)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Employer's Spouse Information */}
            <div className="col-md-4">
              <h5 className="text-primary">Employer&apos;s Spouse Information</h5>

              {spouseFields.map((f, i) =>
                f.as === "select" ? (
                  <Controller
                    key={i}
                    name={f.name}
                    control={control}
                    render={({ field, fieldState }) => (
                      <SelectField
                        id={toId("spouse", f.label, i)}
                        label={f.label}
                         options={f.options}
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    key={i}
                    name={f.name}
                    control={control}
                    render={({ field, fieldState }) => (
                      <Field
                        id={toId("spouse", f.label, i)}
                        label={f.label}
                        type={f.type}
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                )
              )}

              <Controller
                name="spouse_remarks"
                control={control}
                render={({ field, fieldState }) => (
                  <TextAreaField
                    id="spouse-remarks"
                    label="Remarks"
                    error={fieldState.error}
                    {...field}
                  />
                )}
              />

              <h5 className="text-primary mt-4">Contact Info</h5>

              {contactFields.map((f, i) => (
                <Controller
                  key={i}
                  name={f.name}
                  control={control}
                  render={({ field, fieldState }) => (
                    <Field
                      id={toId("contact", f.label, i)}
                      label={f.label}
                      type={f.type}
                      placeholder={f.placeholder}
                      error={fieldState.error}
                      {...field}
                    />
                  )}
                />
              ))}
            </div>

            {/* FDW Job Scope Requirements */}
            <div className="col-md-4">
              <h5 className="text-primary">FDW Job Scope Requirements</h5>

              <Controller
                name="cooking_food"
                control={control}
                render={({ field, fieldState }) => (
                  <Field
                    id="job-cooking"
                    label="Cooking Food"
                    error={fieldState.error}
                    {...field}
                  />
                )}
              />

              {jobScopeChecks.map((item, i) => (
                <Controller
                  key={i}
                  name={item.name}
                  control={control}
                  render={({ field, fieldState }) => (
                    <CheckField
                      id={toId("job", item.label, i)}
                      label={item.label}
                      error={fieldState.error}
                      {...field}
                      checked={field.value}
                    />
                  )}
                />
              ))}

              <Controller
                name="job_remarks"
                control={control}
                render={({ field, fieldState }) => (
                  <TextAreaField
                    id="job-remarks"
                    label="Remarks/Special Requirements"
                    error={fieldState.error}
                    {...field}
                  />
                )}
              />
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-12">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                Submit Form
                {loading && <i className="fa-solid fa-spinner fa-spin ms-2"></i>}
              </button>
              
              {errors.root && (
                <div className="row">
                  <div className="my-4">
                    <p className="text-danger">
                      {errors.root?.message}
                    </p>
                  </div>
                </div>
              )}

              {Object.keys(errors).length > 0 && !errors.root && (
                <div className="row">
                  <div className="my-4">
                    <p className="text-danger">
                      Please correct the following errors:
                    </p>
                    <ul className="text-danger">
                      {Object.entries(errors).map(([fieldName, error]) => (
                        <li key={fieldName}>
                          {error?.message || `${fieldName} is invalid`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default FdwEmployerForm;

// PropTypes
Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
};

SelectField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  required: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
};

TextAreaField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
};

CheckField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
};
